import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

// Allowed file types for upload
const ALLOWED_TYPES = [
  'image/png',
  'image/jpeg',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
]

// Maximum file size: 5MB
const MAX_SIZE = 5 * 1024 * 1024

// Controller for managing process attachments upload modal, drag & drop, validation, and Action Cable subscription
export default class extends Controller {
  static targets = ["modal", "fileInput", "preview", "errors", "errorMessage", "fileName", "fileSize", "dropZone", "form", "submitButton", "deleteModal"]
  static values = {
    processId: Number,
    processType: String,
    errorInvalidType: String,
    errorFileTooLarge: String
  }

  connect() {
    console.log('[ProcessAttachments] Controller connected, processId:', this.processIdValue)
    
    // Store original button text if available
    if (this.hasSubmitButtonTarget) {
      this.originalButtonText = this.submitButtonTarget.getAttribute('data-original-text') || this.submitButtonTarget.textContent.trim()
      this.loadingButtonText = this.submitButtonTarget.getAttribute('data-loading-text') || 'Caricamento...'
    }

    // Subscribe to Action Cable to receive Turbo Stream messages
    // We use turbo_stream_from for automatic subscription, but also subscribe manually as backup
    // The manual subscription processes messages that might not be handled by Turbo automatically
    if (this.hasProcessIdValue) {
      console.log('[ProcessAttachments] Setting up Action Cable subscription for process:', this.processIdValue)
      this.consumer = createConsumer()
      
      // Subscribe to our custom channel to receive broadcasts
      this.subscription = this.consumer.subscriptions.create(
        {
          channel: "ProcessAttachmentsChannel",
          process_id: this.processIdValue
        },
        {
          connected: () => {
            console.log('[ProcessAttachments] Action Cable connected')
          },
          disconnected: () => {
            console.log('[ProcessAttachments] Action Cable disconnected')
          },
          rejected: () => {
            console.error('[ProcessAttachments] Action Cable subscription rejected')
          },
          received: (data) => {
            console.log('[ProcessAttachments] Received message from Action Cable:', data)
            // Process the Turbo Stream message
            this.processTurboStream(data)
          }
        }
      )
    }

    // Listen for Turbo Stream events to update empty state visibility
    this.turboStreamHandler = (event) => {
      this.handleTurboStreamEvent(event)
    }
    document.addEventListener('turbo:before-stream-render', this.turboStreamHandler)
    document.addEventListener('turbo:stream-render', this.turboStreamHandler)
    
    // Also listen for turbo:frame-load to catch any missed updates
    this.frameLoadHandler = () => {
      this.updateTableVisibility()
    }
    document.addEventListener('turbo:frame-load', this.frameLoadHandler)
    
    // Store the pending delete link
    this.pendingDeleteLink = null
    
    // Initial visibility update
    setTimeout(() => {
      this.updateTableVisibility()
    }, 100)
  }
  
  // Process Turbo Stream message received from Action Cable
  processTurboStream(data) {
    console.log('[ProcessAttachments] Processing message from Action Cable:', data)
    
    // Handle different message formats
    if (data.turbo_stream) {
      // Message contains turbo_stream HTML string
      this.processTurboStreamHTML(data.turbo_stream, data.action, data.target)
    } else if (data.action && data.target) {
      // Message contains action and target directly
      this.processStreamAction(data.action, data.target, data.html)
    } else if (typeof data === 'string') {
      // Message is a Turbo Stream HTML string
      this.processTurboStreamHTML(data)
    } else {
      console.warn('[ProcessAttachments] Unknown message format:', data)
    }
    
    // Update visibility after processing
    setTimeout(() => {
      this.updateTableVisibility()
    }, 100)
  }
  
  // Process Turbo Stream HTML string
  processTurboStreamHTML(html, action, target) {
    if (!html) return
    
    // Create a temporary container to parse the Turbo Stream
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    
    // Find all turbo-stream elements
    const turboStreams = tempDiv.querySelectorAll('turbo-stream')
    
    if (turboStreams.length > 0) {
      turboStreams.forEach(stream => {
        const streamAction = stream.getAttribute('action') || action
        const streamTarget = stream.getAttribute('target') || target
        const template = stream.querySelector('template')
        
        this.processStreamAction(streamAction, streamTarget, template)
      })
    } else if (action && target) {
      // No turbo-stream wrapper, process directly
      this.processStreamAction(action, target, html)
    }
  }
  
  // Process a single stream action
  processStreamAction(action, target, content) {
    if (!action || !target) {
      console.warn('[ProcessAttachments] Missing action or target:', { action, target })
      return
    }
    
    const targetElement = document.getElementById(target)
    
    if (!targetElement) {
      console.warn('[ProcessAttachments] Target element not found:', target)
      return
    }
    
    console.log('[ProcessAttachments] Processing action:', action, 'on target:', target, 'with content:', !!content)
    
    switch (action) {
      case 'append':
        if (content) {
          if (typeof content === 'string') {
            // content is HTML string - create a temporary container and append the row
            const tempDiv = document.createElement('tbody')
            tempDiv.innerHTML = content
            // Append the row (tr) element
            while (tempDiv.firstChild) {
              targetElement.appendChild(tempDiv.firstChild)
            }
          } else if (content.content) {
            // content is a template element
            const cloned = content.content.cloneNode(true)
            while (cloned.firstChild) {
              targetElement.appendChild(cloned.firstChild)
            }
          } else {
            // content is already a DOM element
            targetElement.appendChild(content)
          }
          console.log('[ProcessAttachments] Appended content to', target)
        }
        break
      case 'prepend':
        if (content) {
          if (typeof content === 'string') {
            const tempDiv = document.createElement('tbody')
            tempDiv.innerHTML = content
            const firstChild = targetElement.firstChild
            while (tempDiv.firstChild) {
              targetElement.insertBefore(tempDiv.firstChild, firstChild)
            }
          } else if (content.content) {
            const cloned = content.content.cloneNode(true)
            const firstChild = targetElement.firstChild
            while (cloned.firstChild) {
              targetElement.insertBefore(cloned.firstChild, firstChild)
            }
          } else {
            targetElement.insertBefore(content, targetElement.firstChild)
          }
        }
        break
      case 'replace':
        if (content) {
          if (typeof content === 'string') {
            const tempDiv = document.createElement('div')
            tempDiv.innerHTML = content
            targetElement.replaceWith(...Array.from(tempDiv.childNodes))
          } else {
            targetElement.replaceWith(content)
          }
        }
        break
      case 'update':
        targetElement.innerHTML = ''
        if (content) {
          if (typeof content === 'string') {
            const tempDiv = document.createElement('tbody')
            tempDiv.innerHTML = content
            while (tempDiv.firstChild) {
              targetElement.appendChild(tempDiv.firstChild)
            }
          } else if (content.content) {
            const cloned = content.content.cloneNode(true)
            while (cloned.firstChild) {
              targetElement.appendChild(cloned.firstChild)
            }
          } else {
            targetElement.appendChild(content)
          }
        }
        break
      case 'remove':
        targetElement.remove()
        console.log('[ProcessAttachments] Removed element:', target)
        break
    }
  }

  disconnect() {
    // Unsubscribe from Action Cable
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    
    // Remove event listeners
    if (this.turboStreamHandler) {
      document.removeEventListener('turbo:before-stream-render', this.turboStreamHandler)
      document.removeEventListener('turbo:stream-render', this.turboStreamHandler)
    }
    if (this.frameLoadHandler) {
      document.removeEventListener('turbo:frame-load', this.frameLoadHandler)
    }
  }
  
  // Handle delete link click
  handleDeleteClick(event) {
    event.preventDefault()
    event.stopPropagation()
    const link = event.currentTarget
    this.showDeleteModal(link)
  }
  
  // Show delete confirmation modal
  showDeleteModal(link) {
    this.pendingDeleteLink = link
    if (this.hasDeleteModalTarget) {
      this.deleteModalTarget.classList.remove('hidden')
    }
  }
  
  // Confirm delete action
  confirmDelete() {
    if (this.pendingDeleteLink) {
      const url = this.pendingDeleteLink.href
      
      // Close modal first
      this.cancelDelete()
      
      // Create a form and submit it (same way Turbo does for data-turbo-method)
      // This ensures Turbo handles the request correctly
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = url
      form.style.display = 'none'
      
      // Add CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      if (csrfToken) {
        const csrfInput = document.createElement('input')
        csrfInput.type = 'hidden'
        csrfInput.name = 'authenticity_token'
        csrfInput.value = csrfToken
        form.appendChild(csrfInput)
      }
      
      // Add method override for DELETE
      const methodInput = document.createElement('input')
      methodInput.type = 'hidden'
      methodInput.name = '_method'
      methodInput.value = 'delete'
      form.appendChild(methodInput)
      
      // Append to body and submit (Turbo will intercept it)
      document.body.appendChild(form)
      form.requestSubmit()
      
      // Remove form after a short delay
      setTimeout(() => {
        if (form.parentNode) {
          document.body.removeChild(form)
        }
      }, 1000)
    }
  }
  
  // Cancel delete action
  cancelDelete() {
    this.pendingDeleteLink = null
    if (this.hasDeleteModalTarget) {
      this.deleteModalTarget.classList.add('hidden')
    }
  }
  
  // Handle overlay click for delete modal
  overlayClickDelete(event) {
    if (event.target === this.deleteModalTarget) {
      this.cancelDelete()
    }
  }

  // Open the upload modal
  openModal() {
    if (!this.hasModalTarget) return
    this.modalTarget.classList.remove("hidden")
  }

  // Close the upload modal and reset form
  closeModal() {
    if (!this.hasModalTarget) return
    this.modalTarget.classList.add("hidden")
    this.clearFile()
    this.clearError()
  }

  // Handle click on overlay background
  overlayClick(event) {
    if (event.target === this.modalTarget) {
      this.closeModal()
    }
  }

  // Prevent clicks inside modal from closing it
  stopPropagation(event) {
    if (event) {
      event.stopPropagation()
    }
  }

  // Handle drag over event
  handleDragOver(event) {
    event.preventDefault()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.add("border-blue-400", "bg-blue-50")
    }
  }

  // Handle drag leave event
  handleDragLeave(event) {
    event.preventDefault()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("border-blue-400", "bg-blue-50")
    }
  }

  // Handle drop event
  handleDrop(event) {
    event.preventDefault()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("border-blue-400", "bg-blue-50")
    }

    const files = event.dataTransfer.files
    if (files.length > 0) {
      const file = files[0]
      const validation = this.validateFile(file)
      
      if (validation.valid) {
        this.setFile(file)
      } else {
        this.showError(validation.error)
      }
    }
  }

  // Handle file input change
  handleFileSelect(event) {
    const file = event.target.files[0]
    if (file) {
      const validation = this.validateFile(file)
      
      if (validation.valid) {
        this.setFile(file)
      } else {
        this.showError(validation.error)
      }
    }
  }

  // Clear selected file
  clearFile() {
    if (this.hasFileInputTarget) {
      this.fileInputTarget.value = ""
    }
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("hidden")
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }
  }

  // Validate file type and size
  validateFile(file) {
    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: this.hasErrorInvalidTypeValue ? this.errorInvalidTypeValue : "Tipo di file non supportato. Sono consentiti solo PNG, JPG, PDF, DOC, DOCX"
      }
    }

    // Check file size
    if (file.size > MAX_SIZE) {
      return {
        valid: false,
        error: this.hasErrorFileTooLargeValue ? this.errorFileTooLargeValue : "Il file è troppo grande (massimo 5MB)"
      }
    }

    return { valid: true }
  }

  // Set file and show preview
  setFile(file) {
    if (this.hasFileInputTarget) {
      // Create a new FileList with the selected file
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      this.fileInputTarget.files = dataTransfer.files
    }

    this.showPreview(file)
    this.clearError()
    
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
    }
  }

  // Show file preview
  showPreview(file) {
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.remove("hidden")
    }
    
    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = file.name
    }
    
    if (this.hasFileSizeTarget) {
      this.fileSizeTarget.textContent = this.formatFileSize(file.size)
    }
  }

  // Format file size for display
  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i]
  }

  // Show error message
  showError(message) {
    if (this.hasErrorsTarget) {
      this.errorsTarget.classList.remove("hidden")
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
    }
  }

  // Clear error message
  clearError() {
    if (this.hasErrorsTarget) {
      this.errorsTarget.classList.add("hidden")
    }
  }

  // Handle form submission
  handleSubmit(event) {
    const form = event.target
    const formData = new FormData(form)
    
    // Show loading state
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.textContent = this.loadingButtonText || "Caricamento..."
    }

    fetch(form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      credentials: 'same-origin'
    })
    .then(response => {
      const contentType = response.headers.get('content-type')
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      if (contentType && contentType.includes('application/json')) {
        return response.json()
      } else {
        throw new Error('Response is not JSON')
      }
    })
    .then(data => {
      if (data.success) {
        this.closeModal()
        // The table will be updated via Action Cable broadcast
      } else {
        this.showError(data.error || 'Errore durante il caricamento')
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.disabled = false
          this.submitButtonTarget.textContent = this.originalButtonText || "Carica"
        }
      }
    })
    .catch(error => {
      console.error('Upload error:', error)
      this.showError('Errore durante il caricamento')
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false
        this.submitButtonTarget.textContent = this.originalButtonText || "Carica"
      }
    })

    event.preventDefault()
  }

  handleTurboStreamEvent(event) {
    // Check if this is a stream that affects the attachments table
    const stream = event.target
    if (!stream || stream.tagName !== 'TURBO-STREAM') return
    
    const target = stream.getAttribute('target')
    const action = stream.getAttribute('action')
    
    console.log('[ProcessAttachments] Turbo Stream event:', { action, target, eventType: event.type })
    
    if (target && (target === 'attachments-table-body' || target.startsWith('attachment-'))) {
      // This stream affects attachments, update visibility after it renders
      setTimeout(() => {
        this.updateTableVisibility()
      }, 100)
    }
  }
  
  // Update table visibility based on whether there are attachments
  updateTableVisibility() {
    const tableBody = document.getElementById('attachments-table-body')
    const attachmentsTable = document.getElementById('attachments-table')
    const emptyState = document.getElementById('attachments-empty-state')
    
    if (tableBody && attachmentsTable) {
      const hasRows = tableBody.querySelectorAll('tr').length > 0
      
      if (hasRows) {
        // Show table, hide empty state
        if (emptyState) emptyState.style.display = 'none'
      } else {
        // Show empty state
        if (emptyState) emptyState.style.display = 'block'
      }
    }
  }
}
